import { useFormik } from "formik";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  DatePicker,
  Button,
  Label,
} from "@/components/ui";
import { useCreateFinanceProfitMutation, useToast } from "@/hooks";
import { GenerateProfitSchema } from "@/shared";

export function FinanceProfit() {
  const componentPDF = useRef(null);
  const { t } = useTranslation();
  const { toast } = useToast();

  const { mutateAsync, isLoading } = useCreateFinanceProfitMutation();

  const initialValues = {
    start_date: "",
    end_date: "",
  };

  const formik = useFormik({
    initialValues,
    validationSchema: GenerateProfitSchema(),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await mutateAsync(values, {
          onSuccess: () => {
            toast({
              title: t("LABEL.success"),
              description: "Relatório do lucro gerado",
            });
          },
          onError: () => {
            toast({
              title: t("LABEL.error"),
              description: "Erro ao gerar relatório do lucro",
              variant: "destructive",
            });
          },
        });
      } catch {
        toast({
          title: t("LABEL.error"),
          description: t("MESSAGE.error_message"),
          variant: "destructive",
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <section>
      <div className="space-y-10">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink to="/financial/account">
                {t("LABEL.financial")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Lucro</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <form
          onSubmit={formik.handleSubmit}
          className="flex py-10 px-7 rounded-lg border border-zinc-100 shadow-2xl shadow-zinc-200/50 items-center gap-4"
        >
          <div className="flex-1 flex flex-col gap-2">
            <Label>Data inicial</Label>
            <DatePicker
              {...formik.getFieldProps("start_date")}
              isInvalid={
                formik.touched.start_date && !!formik.errors.start_date
              }
              isValid={formik.touched.start_date && !formik.errors.start_date}
              messageError={formik.errors.start_date}
            />
          </div>
          <div className="flex-1 flex flex-col gap-2">
            <Label>Data final</Label>
            <DatePicker
              {...formik.getFieldProps("end_date")}
              isInvalid={formik.touched.end_date && !!formik.errors.end_date}
              isValid={formik.touched.end_date && !formik.errors.end_date}
              messageError={formik.errors.end_date}
            />
          </div>
          <Button
            className="h-10"
            isLoading={isLoading}
            disabled={!formik.isValid}
          >
            Gerar PDF
          </Button>
        </form>

        <div ref={componentPDF} className="w-full">
          {isLoading}
        </div>
      </div>
    </section>
  );
}
